import React from 'react';
import Table from 'react-bootstrap/Table';
import { isPotentialOpener, getPreviousProbablePitcher } from '../utils/mlbBullpen';

// Helper: renders "vs. {name}[*][ (hand)]" + a line beneath with the
// previous probable pitcher when the current probable is a potential opener.
// Numeric prefixes (₁ opener, ₂ true starter) render as HTML <sub>.
const renderMlbPitcherHeader = (game, isHomeSide) => {
  const pitcher = isHomeSide ? game?.homeProbablePitcher : game?.awayProbablePitcher;
  if (!pitcher) return 'vs. TBD';
  const opener = isPotentialOpener(game, isHomeSide);
  const prev = opener ? getPreviousProbablePitcher(game, isHomeSide) : null;
  const throws = pitcher.throws ? ` (${pitcher.throws})` : '';
  return (
    <>
      vs. {opener && <sup>1</sup>}{pitcher.fullName || 'TBD'}{opener ? '*' : ''}{throws}
      {prev && (
        <div style={{ opacity: 0.85 }}>
          <sup>2</sup>{prev.fullName}{prev.throws ? ` (${prev.throws})` : ''}
        </div>
      )}
    </>
  );
};

const DesktopTablesView = ({
  analyticsType,
  groupedColumns,
  awayGroupedColumns,
  homeGroupedColumns,
  performanceView,
  awayVisibleStats,
  homeVisibleStats,
  awayBatters,
  homeBatters,
  safeGame,
  getTeamLogoUrl,
  formatPlayerName,
  getMatchupInfo,
  getStatCellStyle,
  formatStatValue
}) => {
  return (
    <div className="d-flex flex-column" style={{ gap: '20px' }}>
      {/* Away Team Table */}
      <div className="table-container">
        <Table bordered size="sm" className="text-center">
          <thead>
            {(analyticsType === 'performance' && groupedColumns && performanceView === 'all') || (analyticsType === 'pitches' && awayGroupedColumns) ? (
              <>
                <tr>
                  <th className="text-start" style={{ minWidth: '200px' }}>Player</th>
                  {Object.entries(analyticsType === 'pitches' ? awayGroupedColumns : groupedColumns).map(([groupName, groupCols], index, arr) => {
                    // Add 1 to colSpan to account for separator column (except for last group)
                    const colSpanValue = index < arr.length - 1 ? groupCols.length + 1 : groupCols.length;
                    return (
                      <th key={groupName} colSpan={colSpanValue} className="text-center" style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                        {groupName}
                      </th>
                    );
                  })}
                </tr>
                <tr>
                  <th className="text-start" style={{ minWidth: '200px' }}>
                    {analyticsType === 'matchups' ?
                      renderMlbPitcherHeader(safeGame, true) :
                     analyticsType === 'performance' ? (
                       performanceView === 'all' ? 'All' :
                       performanceView === 'last7' ? 'L7' :
                       performanceView === 'last14' ? 'L14' : 'L30'
                     ) : 'Pitches'}
                  </th>
                  {awayVisibleStats.map(stat => (
                    stat.isSeparator ? (
                      <th key={stat.key} style={{ width: '20px', backgroundColor: '#000', border: 'none' }}></th>
                    ) : (
                      <th key={stat.key} style={{ textAlign: 'center', fontSize: '0.7rem' }} title={stat.name}>
                        {stat.label}
                      </th>
                    )
                  ))}
                </tr>
              </>
            ) : (
              <tr>
                <th className="text-start" style={{ 
                  minWidth: '200px',
                  ...(analyticsType === 'matchups' ? { maxWidth: '250px' } : {})
                }}>
                  {analyticsType === 'matchups' ? 
                    (safeGame.homeProbablePitcher ? 
                      `vs. ${safeGame.homeProbablePitcher.fullName || 'TBD'}${isPotentialOpener(safeGame, true) ? '*' : ''}${safeGame.homeProbablePitcher.throws ? ` (${safeGame.homeProbablePitcher.throws})` : ''}` : 
                      'vs. TBD') : 
                   analyticsType === 'performance' ? (
                     performanceView === 'all' ? 'All' :
                     performanceView === 'last7' ? 'L7' :
                     performanceView === 'last14' ? 'L14' : 'L30'
                   ) : 'Pitches'}
                </th>
                {awayVisibleStats.map(stat => (
                  stat.isSeparator ? (
                    <th key={stat.key} style={{ width: '20px', backgroundColor: '#000', border: 'none' }}></th>
                  ) : (
                    <th key={stat.key} style={{ textAlign: 'center', fontSize: '0.7rem' }} title={stat.name}>
                      {stat.label}
                    </th>
                  )
                ))}
                {analyticsType === 'matchups' && <th style={{ minWidth: '1px' }}></th>}
              </tr>
            )}
          </thead>
          <tbody>
            {awayBatters.map((row, index) => {
              // Handle buffer row
              if (row.isBuffer) {
                return (
                  <tr key={`buffer-${index}`} style={{ height: '10px', backgroundColor: '#000' }}>
                    <td colSpan={awayVisibleStats.length + 1 + (analyticsType === 'matchups' ? 1 : 0)} style={{ backgroundColor: '#000', padding: '0', height: '10px' }}></td>
                  </tr>
                );
              }
              
              return (
                <tr key={`${row.batterId}-${row.pitcherId}-${index}`} className={row.isPitcher ? 'pitcher-row' : ''} style={row.isPitcher ? { backgroundColor: 'rgb(26,26,26)' } : {}}>
                  <td 
                    className="player-name-cell"
                    style={{ 
                      backgroundImage: `url(${getTeamLogoUrl(row.batterTeam)})`,
                      backgroundColor: row.isPitcher ? 'rgb(26,26,26)' : 'inherit',
                      ...(row.isPitcher ? { color: analyticsType === 'matchups' ? 'yellow' : 'white' } : {}),
                      ...(analyticsType === 'matchups' ? { maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis' } : {})
                    }}
                  >
                    <span>{row.isPitcher ? `${row.batterName}` : (formatPlayerName ? formatPlayerName(row.batterName, false, true, safeGame.sport) : row.batterName)}</span>
                    {!row.isPitcher && !row.isBuffer && (
                      <span style={{ fontSize: '0.75rem', color: '#6c757d', marginLeft: '8px' }}>
                        {getMatchupInfo(row)}
                      </span>
                    )}
                  </td>
                  {awayVisibleStats.map(stat => (
                    stat.isSeparator ? (
                      <td key={stat.key} style={{ width: '20px', backgroundColor: '#000', border: 'none' }}></td>
                    ) : (
                      <td key={stat.key} style={{
                        ...getStatCellStyle(stat.key, row[stat.key]),
                        ...(row.isPitcher && !getStatCellStyle(stat.key, row[stat.key]).backgroundColor ? { backgroundColor: 'rgb(26,26,26)', backgroundImage: 'none', color: 'white' } : {}),
                        ...(row.isPitcher && getStatCellStyle(stat.key, row[stat.key]).backgroundColor ? { backgroundImage: 'none' } : {})
                      }}
                      title={row.atBats !== undefined ? `${row.atBats} AB` : undefined}>
                        {formatStatValue(row[stat.key], stat.format, row)}
                      </td>
                    )
                  ))}
                  {analyticsType === 'matchups' && <td style={{ minWidth: '1px' }}></td>}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      {/* Home Team Table */}
      <div className="table-container">
        <Table bordered size="sm" className="text-center">
          <thead>
            {(analyticsType === 'performance' && groupedColumns && performanceView === 'all') || (analyticsType === 'pitches' && homeGroupedColumns) ? (
              <>
                <tr>
                  <th className="text-start" style={{ minWidth: '200px' }}>Player</th>
                  {Object.entries(analyticsType === 'pitches' ? homeGroupedColumns : groupedColumns).map(([groupName, groupCols], index, arr) => {
                    // Add 1 to colSpan to account for separator column (except for last group)
                    const colSpanValue = index < arr.length - 1 ? groupCols.length + 1 : groupCols.length;
                    return (
                      <th key={groupName} colSpan={colSpanValue} className="text-center" style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                        {groupName}
                      </th>
                    );
                  })}
                </tr>
                <tr>
                  <th className="text-start" style={{ 
                    minWidth: '200px',
                    ...(analyticsType === 'matchups' ? { maxWidth: '250px' } : {})
                  }}>
                    {analyticsType === 'matchups' ?
                      renderMlbPitcherHeader(safeGame, false) :
                     analyticsType === 'performance' ? (
                       performanceView === 'all' ? 'All' :
                       performanceView === 'last7' ? 'L7' :
                       performanceView === 'last14' ? 'L14' : 'L30'
                     ) : 'Pitches'}
                  </th>
                  {homeVisibleStats.map(stat => (
                    stat.isSeparator ? (
                      <th key={stat.key} style={{ width: '20px', backgroundColor: '#000', border: 'none' }}></th>
                    ) : (
                      <th key={stat.key} style={{ textAlign: 'center', fontSize: '0.7rem' }} title={stat.name}>
                        {stat.label}
                      </th>
                    )
                  ))}
                  {analyticsType === 'matchups' && <th style={{ minWidth: '1px' }}></th>}
                </tr>
              </>
            ) : (
              <tr>
                <th className="text-start" style={{ 
                  minWidth: '200px',
                  ...(analyticsType === 'matchups' ? { maxWidth: '250px' } : {})
                }}>
                  {analyticsType === 'matchups' ? 
                    (safeGame.awayProbablePitcher ? 
                      `vs. ${safeGame.awayProbablePitcher.fullName || 'TBD'}${isPotentialOpener(safeGame, false) ? '*' : ''}${safeGame.awayProbablePitcher.throws ? ` (${safeGame.awayProbablePitcher.throws})` : ''}` : 
                      'vs. TBD') : 
                   analyticsType === 'performance' ? (
                     performanceView === 'all' ? 'All' :
                     performanceView === 'last7' ? 'L7' :
                     performanceView === 'last14' ? 'L14' : 'L30'
                   ) : 'Pitches'}
                </th>
                {homeVisibleStats.map(stat => (
                  stat.isSeparator ? (
                    <th key={stat.key} style={{ width: '20px', backgroundColor: '#000', border: 'none' }}></th>
                  ) : (
                    <th key={stat.key} style={{ textAlign: 'center', fontSize: '0.7rem' }} title={stat.name}>
                      {stat.label}
                    </th>
                  )
                ))}
                {analyticsType === 'matchups' && <th style={{ minWidth: '1px' }}></th>}
              </tr>
            )}
          </thead>
          <tbody>
            {homeBatters.map((row, index) => {
              // Handle buffer row
              if (row.isBuffer) {
                return (
                  <tr key={`buffer-${index}`} style={{ height: '10px', backgroundColor: '#000' }}>
                    <td colSpan={homeVisibleStats.length + 1} style={{ backgroundColor: '#000', padding: '0', height: '10px' }}></td>
                  </tr>
                );
              }
              
              return (
                <tr key={`${row.batterId}-${row.pitcherId}-${index}`} className={row.isPitcher ? 'pitcher-row' : ''} style={row.isPitcher ? { backgroundColor: 'rgb(26,26,26)' } : {}}>
                  <td 
                    className="player-name-cell"
                    style={{ 
                      backgroundImage: `url(${getTeamLogoUrl(row.batterTeam)})`,
                      backgroundColor: row.isPitcher ? 'rgb(26,26,26)' : 'inherit',
                      ...(row.isPitcher ? { color: analyticsType === 'matchups' ? 'yellow' : 'white' } : {}),
                      ...(analyticsType === 'matchups' ? { maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis' } : {})
                    }}
                  >
                    <span>{row.isPitcher ? `${row.batterName}` : (formatPlayerName ? formatPlayerName(row.batterName, false, true, safeGame.sport) : row.batterName)}</span>
                    {!row.isPitcher && !row.isBuffer && (
                      <span style={{ fontSize: '0.75rem', color: '#6c757d', marginLeft: '8px' }}>
                        {getMatchupInfo(row)}
                      </span>
                    )}
                  </td>
                  {homeVisibleStats.map(stat => (
                    stat.isSeparator ? (
                      <td key={stat.key} style={{ width: '20px', backgroundColor: '#000', border: 'none' }}></td>
                    ) : (
                      <td key={stat.key} style={{
                        ...getStatCellStyle(stat.key, row[stat.key]),
                        ...(row.isPitcher && !getStatCellStyle(stat.key, row[stat.key]).backgroundColor ? { backgroundColor: 'rgb(26,26,26)', backgroundImage: 'none', color: 'white' } : {}),
                        ...(row.isPitcher && getStatCellStyle(stat.key, row[stat.key]).backgroundColor ? { backgroundImage: 'none' } : {})
                      }}
                      title={row.atBats !== undefined ? `${row.atBats} AB` : undefined}>
                        {formatStatValue(row[stat.key], stat.format, row)}
                      </td>
                    )
                  ))}
                  {analyticsType === 'matchups' && <td style={{ minWidth: '1px' }}></td>}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default DesktopTablesView;